import { subscribePushNotification, WEB_PUSH_VAPID_PUBLIC_KEY } from '../../auth/_requests';
import { pushStorage } from './pushNotificationStorage';

/** Base64url (VAPID public key) → Uint8Array for PushManager.subscribe. */
export function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

export function isWebPushEnvironmentReady(): boolean {
  if (typeof window === 'undefined') return false;
  if (!window.isSecureContext) return false;
  if (!('serviceWorker' in navigator)) return false;
  if (!('Notification' in window)) return false;
  if (!('PushManager' in window)) return false;
  return true;
}

export async function registerPushServiceWorker(): Promise<ServiceWorkerRegistration> {
  // Script must be under site root (public/), not /src/ — otherwise max scope is /src/ and scope '/' throws.
  const base = import.meta.env.BASE_URL;
  const swUrl = `${base}web-push-sw.js`;
  const registration = await navigator.serviceWorker.register(swUrl, {
    scope: base,
  });
  await navigator.serviceWorker.ready;
  return registration;
}

function subscriptionToToken(sub: PushSubscription): string {
  return JSON.stringify(sub.toJSON());
}

async function ensurePushSubscription(registration: ServiceWorkerRegistration): Promise<PushSubscription> {
  const key = urlBase64ToUint8Array(WEB_PUSH_VAPID_PUBLIC_KEY);
  let sub = await registration.pushManager.getSubscription();

  if (sub == null) {
    sub = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: key as BufferSource,
    });
    return sub;
  }

  const existingKey = sub.options.applicationServerKey;
  const sameKey =
    existingKey != null &&
    existingKey.byteLength === key.byteLength &&
    new Uint8Array(existingKey).every((b, i) => b === key[i]);

  if (!sameKey) {
    await sub.unsubscribe();
    sub = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: key as BufferSource,
    });
  }

  return sub;
}

export async function syncPushSubscriptionWithBackend(apiToken: string, userId: number): Promise<void> {
  const registration = await registerPushServiceWorker();
  const sub = await ensurePushSubscription(registration);
  const token = subscriptionToToken(sub);

  const previous = pushStorage.getRegisteredToken(userId);
  if (previous === token) return;

  await subscribePushNotification(apiToken, {
    clientType: 'web',
    token,
  });
  pushStorage.setRegisteredToken(userId, token);
}

export async function subscribeAfterUserConsent(
  apiToken: string,
  userId: number
): Promise<{ ok: boolean; denied: boolean }> {
  const perm = await Notification.requestPermission();
  if (perm !== 'granted') {

    return { ok: false, denied: perm === 'denied' };
  }

  try {
    await syncPushSubscriptionWithBackend(apiToken, userId);
    return { ok: true, denied: false };
  } catch {
    return { ok: false, denied: false };
  }
}

export async function revokeWebPushOnDeviceForUser(userId: number | undefined): Promise<void> {
  if (userId == null) return;

  try {
    const reg = await navigator.serviceWorker.getRegistration();
    const sub = await reg?.pushManager.getSubscription();
    await sub?.unsubscribe();
  } catch (err) {
    if (import.meta.env.DEV) {
      console.warn('[web push] Failed to revoke push subscription:', err);
    }
  } finally {
    pushStorage.clearRegisteredToken(userId);
  }
}
